import { useState } from "react";
import { MapPin, Phone, Mail, Clock, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { SITE } from "../data/site";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    setSent(true);
  };
  return (
    <>
      <PageHero
        eyebrow="Get in touch"
        title={<>Let's engineer<br/><span className="text-[var(--primary)]">something.</span></>}
        subtitle="Tell us about your project. A senior engineer will review and respond personally within one business day."
        image="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1920&q=80"
      />

      <section className="py-24 md:py-32 container-wide grid md:grid-cols-12 gap-16">
        {/* Left — details */}
        <Reveal className="md:col-span-5">
          <div className="eyebrow mb-6">Visit · Call · Write</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight">Melbourne engineered. Australia deployed.</h2>

          <div className="mt-12 space-y-8">
            <Detail icon={MapPin} title="Headquarters" text={SITE.address} link="#" linkText="Get directions" />
            <Detail icon={Phone} title="Phone" text={SITE.phone} link={`tel:${SITE.phone}`} linkText="Call now" />
            <Detail icon={Mail} title="Email" text={SITE.email} link={`mailto:${SITE.email}`} linkText="Send email" />
            <Detail icon={Clock} title="Hours" text="Mon – Fri · 8:00 AM – 5:30 PM AEDT" sub="24/7 critical support available for contracted clients" />
          </div>

          {/* Map placeholder */}
          <div className="mt-12 aspect-[16/10] bg-[#0a0a0a] relative overflow-hidden">
            <iframe
              title="UMD Office Map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=145.0488%2C-37.9230%2C145.0689%2C-37.9100&layer=mapnik&marker=-37.9165%2C145.0588"
              className="absolute inset-0 w-full h-full grayscale contrast-125"
              loading="lazy"
            />
          </div>
        </Reveal>

        {/* Right — form */}
        <div className="md:col-span-6 md:col-start-7">
          <div className="bg-[#0a0a0a] text-white p-8 md:p-12">
            <div className="font-mono-tech text-[11px] tracking-[0.25em] uppercase text-[var(--primary)] mb-6">// New enquiry</div>
            <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-tight mb-10">Tell us about<br/>your project.</h3>

            {sent ? (
              <div className="py-12 text-center" data-testid="contact-success">
                <CheckCircle2 size={56} className="text-[var(--primary)] mx-auto" />
                <h4 className="font-display text-2xl mt-6 tracking-tight">Enquiry received.</h4>
                <p className="text-white/60 mt-4 max-w-sm mx-auto">A senior engineer will review your project and respond within one business day.</p>
                <button onClick={() => setSent(false)} className="btn-outline btn-outline-light mt-10 mx-auto">Send another <ArrowUpRight size={14}/></button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-2" data-testid="contact-form">
                <Field name="name" label="Full name" testid="contact-name" />
                <Field name="company" label="Company" testid="contact-company" />
                <Field name="email" type="email" label="Work email" testid="contact-email" />
                <Field name="phone" label="Phone (optional)" testid="contact-phone" />
                <div className="pt-6">
                  <label className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/40">Project category</label>
                  <select required data-testid="contact-category" className="input-clean input-light mt-1 bg-transparent">
                    <option value="" className="text-black">Select category…</option>
                    <option className="text-black">Engineering & IoT</option>
                    <option className="text-black">Project Management</option>
                    <option className="text-black">System Integration</option>
                    <option className="text-black">Manufacturing</option>
                    <option className="text-black">Consulting</option>
                    <option className="text-black">Asset Management</option>
                  </select>
                </div>
                <div className="pt-6">
                  <label className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/40">Project brief</label>
                  <textarea required data-testid="contact-message" rows={4} placeholder="Outline your problem, scale and timeline…" className="input-clean input-light mt-1 resize-none" />
                </div>
                <div className="pt-10">
                  <button type="submit" data-testid="contact-submit" className="btn-primary w-full justify-between">Send enquiry <ArrowUpRight size={18} className="arrow"/></button>
                </div>
                <p className="text-xs text-white/40 pt-4">By submitting you agree to UMD's privacy policy. We never share enquiries.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

const Field = ({ name, label, type = "text", testid }) => (
  <div className="pt-6">
    <label className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/40">{label}</label>
    <input required type={type} name={name} data-testid={testid} className="input-clean input-light mt-1" />
  </div>
);

const Detail = ({ icon: Icon, title, text, sub, link, linkText }) => (
  <div className="flex gap-5">
    <div className="w-12 h-12 border border-[var(--border)] flex items-center justify-center shrink-0">
      <Icon size={18} className="text-[var(--primary)]"/>
    </div>
    <div>
      <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[var(--muted-fg)]">{title}</div>
      <div className="font-display text-lg md:text-xl tracking-tight mt-1">{text}</div>
      {sub && <div className="text-sm text-[var(--muted-fg)] mt-2">{sub}</div>}
      {link && <a href={link} className="btn-ghost mt-3">{linkText} <ArrowUpRight size={12}/></a>}
    </div>
  </div>
);
